import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import type { FieldArrayWithId } from 'react-hook-form';

export interface ContentBlock {
  type: 'image' | 'video' | 'text';
  src?: string;
  alt?: string;
  caption?: string;
  content?: string;
}

export interface NewGame {
  slug: string;
  title: string;
  description: string;
  image_icon: string;
  download_url?: string;
  download_title?: string;
  status: 'in_progress' | 'released' | 'coming_soon';
  is_unreleased?: boolean;
  is_visible: boolean;
  content_blocks: ContentBlock[];
}

interface AddGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newGame: NewGame) => Promise<void>;
}

export const AddGameModal: React.FC<AddGameModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm<NewGame>({
    defaultValues: {
      status: 'coming_soon',
      is_unreleased: true,
      download_title: 'دانلود بازی',
      download_url: '',
      is_visible: true,
      content_blocks: [],
    },
  });

  const isUnreleased = watch('is_unreleased');

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content_blocks',
  });

  const onSubmit = async (data: NewGame) => {
    try {
      setIsSaving(true);
      const payload: NewGame = {
        ...data,
        status: data.is_unreleased ? 'coming_soon' : data.status,
        download_url: data.download_url ? data.download_url.trim() : '',
        download_title: data.download_title ? data.download_title.trim() : 'دانلود بازی',
      };
      await onSave(payload);
      onClose();
    } catch (error) {
      console.error('Error saving game:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            dir="rtl"
          >
            <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
              <h2 className="text-2xl font-bold text-white">🎮 افزودن بازی جدید</h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-white text-xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    شناسه اختصاصی (اسلاگ / Slug) *
                  </label>
                  <input
                    {...register('slug', { required: true })}
                    placeholder="مثال: stop-duck"
                    dir="ltr"
                    className="w-full bg-gray-800 border border-gray-700 focus:border-primary text-white rounded-lg p-2.5"
                  />
                  {errors.slug && <span className="text-red-400 text-xs">ورود اسلاگ الزامی است.</span>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    عنوان بازی *
                  </label>
                  <input
                    {...register('title', { required: true })}
                    placeholder="مثال: Stop Duck"
                    className="w-full bg-gray-800 border border-gray-700 focus:border-primary text-white rounded-lg p-2.5"
                  />
                  {errors.title && <span className="text-red-400 text-xs">ورود عنوان الزامی است.</span>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  توضیحات بازی *
                </label>
                <textarea
                  {...register('description', { required: true })}
                  placeholder="توضیحاتی در مورد گیم‌پلی، داستان و ویژگی‌های بازی بنویسید..."
                  className="w-full bg-gray-800 border border-gray-700 focus:border-primary text-white rounded-lg p-2.5"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  آدرس تصویر شاخص / آیکون (URL) *
                </label>
                <input
                  {...register('image_icon', { required: true })}
                  placeholder="https://..."
                  dir="ltr"
                  className="w-full bg-gray-800 border border-gray-700 focus:border-primary text-white rounded-lg p-2.5"
                />
              </div>

              {/* تنظیمات لینک دانلود سفارشی و وضعیت انتشار */}
              <div className="bg-gray-800/60 p-4 rounded-xl border border-white/10 space-y-4">
                <h3 className="text-base font-bold text-amber-400 flex items-center gap-2">
                  <span>📥 تنظیمات لینک و دکمه دانلود</span>
                </h3>

                <div className="bg-black/40 p-3.5 rounded-xl border border-amber-500/20">
                  <label className="flex items-center text-sm font-bold text-amber-300 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      {...register('is_unreleased')}
                      className="ml-2.5 w-4 h-4 text-primary rounded accent-amber-500"
                    />
                    <span>این بازی هنوز منتشر نشده است (Coming Soon / به‌زودی)</span>
                  </label>
                  <p className="text-xs text-gray-400 mt-1.5 mr-6 leading-relaxed">
                    با فعال‌سازی این گزینه، دکمه دانلود در صفحه بازی نشان داده نخواهد شد و در عوض وضعیت «هنوز منتشر نشده است - به‌زودی» به همراه نشانگر ویژه نمایش می‌یابد.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      عنوان دکمه دانلود (اختیاری)
                    </label>
                    <input
                      {...register('download_title')}
                      placeholder="مثلاً: دریافت بازی، Get the Demo، دانلود نسخه ویندوز"
                      className="w-full bg-gray-800 border border-gray-700 focus:border-primary text-white rounded-lg p-2.5"
                    />
                    <span className="text-[11px] text-gray-400 mt-1 block">
                      در صورت خالی گذاشتن، عبارت پیش‌فرض «دانلود بازی» نمایش داده می‌شود.
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      آدرس دانلود (اختیاری)
                    </label>
                    <input
                      {...register('download_url')}
                      placeholder="https://..."
                      dir="ltr"
                      disabled={isUnreleased}
                      className="w-full bg-gray-800 border border-gray-700 focus:border-primary text-white rounded-lg p-2.5 disabled:opacity-40"
                    />
                    <span className="text-[11px] text-gray-400 mt-1 block">
                      اجباری نیست. اگر آدرسی وارد نشود، وضعیت بازی غیرقابل دانلود اعلام می‌شود.
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    وضعیت توسعه بازی
                  </label>
                  <select
                    {...register('status')}
                    disabled={isUnreleased}
                    className="w-full bg-gray-800 border border-gray-700 focus:border-primary text-white rounded-lg p-2.5 disabled:opacity-40"
                  >
                    <option value="in_progress">در حال ساخت (In Progress)</option>
                    <option value="released">ساخته شده / آماده (Released)</option>
                    <option value="coming_soon">به‌زودی (Coming Soon)</option>
                  </select>
                </div>
                <div className="flex items-center pt-6">
                  <label className="flex items-center text-gray-300 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      {...register('is_visible')}
                      className="ml-2 w-4 h-4 accent-primary"
                    />
                    <span>قابل مشاهده بودن در سایت (عمومی)</span>
                  </label>
                </div>
              </div>

              {/* بلوک‌های محتوا */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-white">بلوک‌های محتوا (ویدیو، تصویر، متن)</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => append({ type: 'image', src: '', alt: '' })}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs"
                    >
                      + افزودن تصویر
                    </button>
                    <button
                      type="button"
                      onClick={() => append({ type: 'video', src: '', caption: '' })}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs"
                    >
                      + افزودن ویدیو (YouTube/Aparat)
                    </button>
                    <button
                      type="button"
                      onClick={() => append({ type: 'text', content: '' })}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg text-xs"
                    >
                      + افزودن متن
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {fields.map((field: FieldArrayWithId<NewGame, 'content_blocks'>, index: number) => (
                    <div key={field.id} className="bg-gray-800/80 p-4 rounded-xl border border-gray-700">
                      <div className="flex justify-between items-center mb-3">
                        <select
                          {...register(`content_blocks.${index}.type`)}
                          className="bg-gray-700 text-white rounded-lg p-1.5 text-xs"
                        >
                          <option value="image">تصویر</option>
                          <option value="video">ویدیو (یوتیوب / آپارات / مستقیم)</option>
                          <option value="text">متن توضیحی</option>
                        </select>
                        <div className="space-x-2 space-x-reverse">
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => move(index, index - 1)}
                              className="text-gray-400 hover:text-white text-xs px-2 py-1 bg-gray-700 rounded"
                            >
                              ↑ بالا
                            </button>
                          )}
                          {index < fields.length - 1 && (
                            <button
                              type="button"
                              onClick={() => move(index, index + 1)}
                              className="text-gray-400 hover:text-white text-xs px-2 py-1 bg-gray-700 rounded"
                            >
                              ↓ پایین
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="text-red-400 hover:text-red-300 text-xs px-2 py-1 bg-red-950/40 rounded border border-red-800/40"
                          >
                            حذف
                          </button>
                        </div>
                      </div>

                      {field.type === 'image' && (
                        <div className="space-y-2">
                          <input
                            {...register(`content_blocks.${index}.src`)}
                            placeholder="آدرس تصویر (URL)"
                            dir="ltr"
                            className="w-full bg-gray-700 text-white rounded-lg p-2 text-sm"
                          />
                          <input
                            {...register(`content_blocks.${index}.alt`)}
                            placeholder="متن جایگزین (Alt Text برای سئو)"
                            className="w-full bg-gray-700 text-white rounded-lg p-2 text-sm"
                          />
                        </div>
                      )}

                      {field.type === 'video' && (
                        <div className="space-y-2">
                          <input
                            {...register(`content_blocks.${index}.src`)}
                            placeholder="آدرس ویدیو یوتیوب، آپارات یا فایل مستقیم (مثال: https://youtu.be/CImSZwgxsp8)"
                            dir="ltr"
                            className="w-full bg-gray-700 text-white rounded-lg p-2 text-sm font-mono"
                          />
                          <input
                            {...register(`content_blocks.${index}.caption`)}
                            placeholder="عنوان یا توضیح ویدیو (اختیاری)"
                            className="w-full bg-gray-700 text-white rounded-lg p-2 text-sm"
                          />
                        </div>
                      )}

                      {field.type === 'text' && (
                        <textarea
                          {...register(`content_blocks.${index}.content`)}
                          placeholder="متن توضیحی بازی..."
                          className="w-full bg-gray-700 text-white rounded-lg p-2 text-sm"
                          rows={3}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl text-gray-300 hover:bg-gray-800 text-sm"
                >
                  انصراف
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="bg-primary hover:bg-primary/90 text-black font-extrabold px-6 py-2.5 rounded-xl text-sm transition-all disabled:opacity-50"
                >
                  {isSaving ? 'در حال ثبت...' : 'ذخیره بازی'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
